import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const createDocCategory = createAsyncThunk(
  "createDocCategory",
  async ({ url, body, token }) => {
    try {
      const response = await axios.post(url, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const updateDocCategory = createAsyncThunk(
  "updateDocCategory",
  async ({ url, body, token }) => {
    try {
      const response = await axios.patch(url, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

// Common delete async thunk for both categories and folders
export const deleteDocItem = createAsyncThunk(
  "deleteDocItem",
  async ({ url, token, itemType = "item" }) => {
    try {
      const response = await axios.delete(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return { data: response?.data, itemType };
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const createDocFolder = createAsyncThunk(
  "createDocFolder",
  async ({ url, body, token }) => {
    try {
      const response = await axios.post(url, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response?.data;
    } catch (error) {
      throw error?.response?.data || error?.message;
    }
  }
);

export const getAlldocCategory = createAsyncThunk(
  "getAlldocCategory",
  async ({ url, token, page, pageSize }) => {
    try {
      let paginatedUrl = url;
      const params = [];
      if (page !== undefined) {
        params.push(`page=${page}`);
      }
      if (pageSize !== undefined) {
        params.push(`page_size=${pageSize}`);
      }
      if (params.length > 0) {
        const separator = url.includes("?") ? "&" : "?";
        paginatedUrl = `${url}${separator}${params.join("&")}`;
      }

      const response = await axios.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get document category");
      throw error.response?.data || error.message;
    }
  }
);

export const getAllDocFolder = createAsyncThunk(
  "getAllDocFolder",
  async ({ url, token, page, pageSize }) => {
    try {
      let paginatedUrl = url;
      if (page !== undefined && pageSize !== undefined) {
        const separator = url?.includes("?") ? "&" : "?";
        paginatedUrl = `${url}${separator}page=${page}&page_size=${pageSize}`; // url modify with page and page size
      }

      const response = await axios?.get(paginatedUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      return {
        ...response.data,
        pagination: {
          page: page || 1,
          pageSize: pageSize || 10,
          rowCount: response.data.count || 0,
          nextPage: response.data.next,
          previousPage: response.data.previous,
        },
      };
    } catch (error) {
      toast.error("Failed to get document folder");
      throw error.response?.data || error.message;
    }
  }
);

export const updateDocCategoryOrFolder = createAsyncThunk(
  "updateDocCategoryOrFolder",
  async ({ url, body, token }) => {
    try {
      const response = await axios.patch(url, body, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
);

export const updateDocFolder = createAsyncThunk(
  "document/updateDocFolder",
  async ({ url, body, token }, { rejectWithValue }) => {
    try {
      const response = await axios.put(url, body, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFolderById = createAsyncThunk(
  "getFolderById",
  async ({ url, token, folderId }) => {
    try {
      const response = await axios.get(`${url}${folderId}/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      toast.error("Failed to get folder details");
      throw error.response?.data || error.message;
    }
  }
);

const DocumentSlice = createSlice({
  name: "DocumentSlice",
  initialState: {
    allDocCategory: null,
    docCategoryPagination: {
      page: 1,
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    allDocFolder: null,
    docFoldersPagination: {
      pageSize: 10,
      rowCount: 0,
      nextPage: null,
      previousPage: null,
    },
    currentFolderData: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createDocCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDocCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createDocCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create document category";
      })
      .addCase(updateDocCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDocCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update document category";
      })
      .addCase(deleteDocItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocItem.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteDocItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete item";
      })
      .addCase(getAlldocCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAlldocCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.allDocCategory = action?.payload?.results;
        state.docCategoryPagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAlldocCategory.rejected, (state, action) => {
        state.loading = false;
        state.allDocCategory =
          action?.payload || "Failed to get document category";
      })
      .addCase(updateDocCategoryOrFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocCategoryOrFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDocCategoryOrFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to edit document category";
      })
      .addCase(updateDocFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateDocFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to edit document folder";
      })
      .addCase(createDocFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDocFolder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createDocFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create document folder";
      })
      .addCase(getAllDocFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllDocFolder.fulfilled, (state, action) => {
        state.loading = false;
        state.allDocFolder = action?.payload?.results;
        state.docFoldersPagination = {
          ...action?.payload?.pagination,
          count: action?.payload?.count,
          next: action?.payload?.next,
          previous: action?.payload?.previous,
        };
      })
      .addCase(getAllDocFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get document folder";
      })
      .addCase(getFolderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFolderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFolderData = action.payload;
      })
      .addCase(getFolderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to get folder details";
      });
  },
});

export const DocumentSliceReducer = DocumentSlice.reducer;
